import React, {FunctionComponent} from 'react';
import {FormattedMessage} from 'react-intl';

import './styles.scss';

const PRIVACY_POLICY_URL = 'https://mira-now.scalable.care/privacy';

const linkValues = (href: string) => ({
  a: (chunks: React.ReactNode) => <a href={href} target="_blank" rel="noopener noreferrer">{chunks}</a>
});

export const Term: FunctionComponent = () => {
  return <div className="term">
    <h1>
      <FormattedMessage id={'gritx.terms.title'} defaultMessage={'Terms of Use'}/>
    </h1>
    <p className="term__updated">
      <FormattedMessage id={'gritx.terms.updated'} defaultMessage={'Last updated July 13, 2026'}/>
    </p>

    <h2><span className="term__num">1.</span><FormattedMessage id={'gritx.terms.s1.title'} defaultMessage={'Agreement'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s1.c1'}
          defaultMessage={'This Agreement describes the Terms of Use for use of Mira Now and governs your access to and use of the app provided by Scalable Care. You should read this Agreement carefully before using or accessing Mira Now. By using Mira Now, you are entering into this Agreement and are agreeing to the terms of this Agreement.'}
        />
      </li>
      <li>
        <FormattedMessage
          id={'gritx.terms.s1.c2'}
          defaultMessage={'Scalable Care may revise our Terms of Use at any time by updating this Agreement and posting it. You should periodically visit either our website or application and review the Terms of Use to determine if any changes have been made. We may also notify you by email of any material changes to this Agreement. Your continued use of Mira Now after any changes have been made to the Terms of Use signifies and confirms your acceptance of any such changes or amendments to the Terms of Use.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">2.</span><FormattedMessage id={'gritx.terms.s2.title'} defaultMessage={'Privacy'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s2.c1'}
          defaultMessage={'Protecting information you provide is important to us. Please review our <a>privacy policy</a> ("Privacy Policy"). By using or accessing Mira Now, you are also agreeing to the terms of the Privacy Policy.'}
          values={linkValues(PRIVACY_POLICY_URL)}
        />
      </li>
    </ol>

    <h2><span className="term__num">3.</span><FormattedMessage id={'gritx.terms.s3.title'} defaultMessage={'Ownership'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s3.c1'}
          defaultMessage={'Scalable Care retains all ownership rights, title and interest (including all intellectual property rights) worldwide to the Services, including to all software and content. No rights are granted to you other than as expressly set forth in these terms. All trademarks, service marks and trade names are owned by Scalable Care. Except for any information that is in the public domain, you are not authorized to reproduce, transmit or distribute the proprietary information connected with Mira Now. Under no circumstances will you acquire any ownership rights or other interest in any content by or through your use and/or access to Mira Now. By posting information either through our website or our application, you represent that you have the right to grant permission for use by Scalable Care.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">4.</span><FormattedMessage id={'gritx.terms.s4.title'} defaultMessage={'Site use'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s4.c1'}
          defaultMessage={'Scalable Care grants you a limited, revocable, nonexclusive license to use Mira Now solely for your own personal use and not for republication, distribution, assignment, sublicense, sale, preparation of derivative works, or other use. You agree not to copy materials accessed, reverse engineer or break into either our website and/or our application, or use Scalable Care\'s materials, products or Services in violation of any law. The use and access of Mira Now is at the discretion of Scalable Care and may terminate your use and/or access of Mira Now at any time for any reason.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">5.</span><FormattedMessage id={'gritx.terms.s5.title'} defaultMessage={'Disclaimer'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s5.c1'}
          defaultMessage={'The information on our website and/or mobile application is provided on an "as is", "as available" basis. You agree that use of Mira Now is at your sole risk. Scalable Care disclaims all warranties of any kind, including but not limited to any express warranties, statutory warranties, and any implied warranties of merchantability, fitness for a particular purpose, and non-infringement. To the extent your jurisdiction does not allow limitations on warranties, this limitation may not apply to you. Your sole and exclusive remedy relating to your use of Mira Now shall be to discontinue accessing those Services.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">6.</span><FormattedMessage id={'gritx.terms.s6.title'} defaultMessage={'Limitation of liability'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s6.c1'}
          defaultMessage={'Under no circumstances will Scalable Care be liable or responsible for any direct, indirect, incidental, consequential (including damages from loss of business, lost profits, litigation, or the like), special, exemplary, punitive or other damages, under any legal theory, arising out of or in any way relating to Mira Now, your use of Mira Now, or content created in connection with Mira Now, even if advised of the possibility of such damages. Your sole remedy for dissatisfaction with Mira Now and/or content is to cease your use of Mira Now.'}
        />
      </li>
      <li>
        <FormattedMessage
          id={'gritx.terms.s6.c2'}
          defaultMessage={'You may have additional rights under certain laws which do not allow the exclusion of implied warranties, or the exclusion or limitation of certain damages. If these laws apply to you, the exclusions or limitations in this Agreement that directly conflict with such laws may not apply to you.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">7.</span><FormattedMessage id={'gritx.terms.s7.title'} defaultMessage={'Use of information'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s7.c1'}
          defaultMessage={'Scalable Care reserves the right, and you authorize us, to use and assign all information regarding the use by you of Mira Now and all information provided by you in any manner consistent with our Privacy Policy.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">8.</span><FormattedMessage id={'gritx.terms.s8.title'} defaultMessage={'Applicable law'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s8.c1'}
          defaultMessage={'You agree that the laws of the State of Delaware, without regard to conflicts of laws provisions, will govern this Agreement and any dispute that may arise between you and Scalable Care or its affiliates.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">9.</span><FormattedMessage id={'gritx.terms.s9.title'} defaultMessage={'Severability'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s9.c1'}
          defaultMessage={'If any provision of this Agreement shall be adjudged by any court of competent jurisdiction to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that this Agreement will otherwise remain in full force and effect.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">10.</span><FormattedMessage id={'gritx.terms.s10.title'} defaultMessage={'Waiver'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s10.c1'}
          defaultMessage={'The failure of Scalable Care to exercise or enforce any right or provision of this Agreement shall not operate as a waiver of such right or provision. Any waiver of this Agreement by Scalable Care must be in writing and signed by an authorized representative of Scalable Care.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">11.</span><FormattedMessage id={'gritx.terms.s11.title'} defaultMessage={'Termination'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s11.c1'}
          defaultMessage={'Scalable Care may terminate this Agreement at any time, with or without notice, for any reason.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">12.</span><FormattedMessage id={'gritx.terms.s12.title'} defaultMessage={'Relationship of the parties'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s12.c1'}
          defaultMessage={'Nothing contained in this Agreement or your use of Mira Now shall be construed to constitute either party as a partner, joint venturer, employee or agent of the other party, nor shall either party hold itself out as such. Neither party has any right or authority to incur, assume or create, in writing or otherwise, any warranty, liability or other obligation of any kind, express or implied, in the name of or on behalf of the other party, it being intended by both parties that each shall remain independent contractors responsible for its own actions.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">13.</span><FormattedMessage id={'gritx.terms.s13.title'} defaultMessage={'Entire agreement'}/></h2>
    <ol>
      <li>
        <FormattedMessage
          id={'gritx.terms.s13.c1'}
          defaultMessage={'This Agreement constitutes the entire Agreement between you and Scalable Care and governs your access and/or use of Mira Now, and supersedes all prior or contemporaneous communications and proposals, whether electronic, oral or written, between you and Scalable Care with respect to Mira Now. Notwithstanding the foregoing, you may also be subject to additional Terms of Use, posted policies (including but not limited to Scalable Care\'s Privacy Policy), guidelines or rules that may apply when you use the website or mobile application.'}
        />
      </li>
    </ol>

    <h2><span className="term__num">14.</span><FormattedMessage id={'gritx.terms.s14.title'} defaultMessage={'Contact information'}/></h2>
    <address>
      <FormattedMessage id={'gritx.terms.s14.address'} defaultMessage={'Address: PO Box 432750, San Francisco, CA, 94142, USA'}/>
      <br/>
      <FormattedMessage id={'gritx.terms.s14.phone'} defaultMessage={'Phone: 866-279-4820'}/>
    </address>
  </div>;
};
